import { Flex } from "antd";
import React from "react";
import Barcode from "react-barcode";

interface PrintProp {
  data: any;
}

const Bill: React.FC<PrintProp> = ({ data }) => {
  const getCurrencySymbol = (currency: "LAK" | "THB" | "USD") => {
    switch (currency) {
      case "LAK":
        return "₭";
      case "THB":
        return "฿";
      case "USD":
        return "$";
      default:
        return "";
    }
  };

  const getPaymentStatus = (
    status: "pending" | "partial" | "paid" | "refunded"
  ) => {
    switch (status) {
      case "pending":
        return "ລໍຖ້າຊຳລະ";
      case "partial":
        return "ຊຳລະບາງສ່ວນ";
      case "paid":
        return "ຊຳລະແລ້ວ";
      case "refunded":
        return "ຄືນເງິນ";
      default:
        return "";
    }
  };

  const getPaymentMethod = (status: "cash" | "transfer" | "qr" | "card") => {
    switch (status) {
      case "cash":
        return "ເງິນສົດ";
      case "transfer":
        return "ເງິນໂອນ";
      case "qr":
        return "ຄິວອາ";
      case "card":
        return "ບັດສະມາສິກ";
      default:
        return "";
    }
  };

  const currencySymbol = getCurrencySymbol(data?.currency);

  const formatPrice = (price: number) => {
    return price?.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Truncate text to fit 80mm width (~40-48 chars)
  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength - 3) + "..."
      : text.padEnd(maxLength, " ");
  };

  return (
    <div
      style={{
        fontFamily: "monospace",
        width: "80mm",
        fontSize: "12px",
        lineHeight: "1.2",
        padding: "5mm",
      }}
    >
      <center>
        <img
          src="/poslogo.png"
          style={{ width: 90, height: 90, objectFit: "cover" }}
        />
      </center>

      <div style={{ textAlign: "center", marginBottom: "5mm" }}>
        <h1 style={{ fontSize: "14px", margin: "0",fontWeight:'900' }}>
          ໃບຮັບເງິນ - Receipt
        </h1>
        <div style={{ fontSize: "10px" }}>
          <p style={{ margin: "2px 0" }}>ເລກທີ: {data?.receiptNumber}</p>
          <p style={{ margin: "2px 0" }}>
            ວັນທີ:{" "}
            {new Date(data?.createdAt).toLocaleString("th-TH", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </p>
          <p style={{ margin: "2px 0" }}>
            ອອກໂດຍ: {truncateText(data?.issuedBy?.name || "ไม่ระบุ", 30)}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: "5mm" }}>
        <h3 style={{ fontSize: "12px", margin: "2px 0",fontWeight:'900' }}>ຂໍ້ມູນລູກຄ້າ</h3>
        <p style={{ margin: "2px 0" }}>
          ຊື່: {truncateText(data?.customer?.name || "ไม่ระบุ", 30)}
        </p>
        {data?.customer?.phone && (
          <p style={{ margin: "2px 0" }}>ເບີໂທ: {data?.customer.phone}</p>
        )}
      </div>

      <h3 style={{ fontSize: "12px", margin: "2px 0",fontWeight:'900' }}>ລາຍການ</h3>
      <div style={{ marginBottom: "5mm" }}>
        {data?.items.map((item: any, index: number) => (
          <div
            key={index}
            style={{
              marginBottom: "3mm",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <p style={{ margin: "1px 0" }}>
              - {truncateText(
                `${item.course?.name || "ບໍ່ລະບຸ"} (${
                  item.packageOption?.sessions
                } ຄັ້ງ)`,
                30
              )}
            </p>
            <p style={{ margin: "1px 0" }}>
              {item.quantity} x {currencySymbol}
              {formatPrice(
                item.packageOption?.price[data.currency] || 0
              )} = {currencySymbol}
              {formatPrice(item.subtotal[data.currency] || 0)}
            </p>
          </div>
        ))}
      </div>

      <div
        style={{ textAlign: "right", marginBottom: "5mm", lineHeight: "20px" }}
      >
        <p style={{ margin: "1px 0" }}>
          ລວມ: {currencySymbol}
          {formatPrice(data?.subtotal)}
        </p>
        <p style={{ margin: "1px 0" }}>
          ສ່ວນຫຼຸດ: {currencySymbol}
          {formatPrice(data?.discount)}
        </p>
        {/* <p style={{ margin: '1px 0' }}>ພາສີ: {currencySymbol}{formatPrice(data.tax)}</p> */}
        <p style={{ margin: "1px 0", fontWeight: "bold" }}>
          ລວມທັງໝົດ: {currencySymbol}
          {formatPrice(data?.total)}
        </p>
      </div>

      <div style={{ marginBottom: "5mm" }}>
        <h3 style={{ fontSize: "12px", margin: "2px 0",fontWeight:'900' }}>ການຊຳລະ</h3>
        <p style={{ margin: "1px 0" }}>
          ສະຖານະ: {getPaymentStatus(data?.paymentStatus)}
        </p>
        <p style={{ margin: "1px 0" }}>
          ຮູບແບບຊຳລະ: {getPaymentMethod(data?.paymentMethod)}
        </p>
        <p style={{ margin: "1px 0" }}>
          ຊຳລະແລ້ວ: {currencySymbol}
          {formatPrice(data?.amountPaid)}
        </p>
        <p style={{ margin: "1px 0" }}>
          ຄ້າງຈ່າຍ: {currencySymbol}
          {formatPrice(data?.amountDue)}
        </p>
      </div>

      {data?.notes && (
        <div style={{ marginBottom: "5mm" }}>
          <h3 style={{ fontSize: "12px", margin: "2px 0" }}>ໝາຍເຫດ</h3>
          <p style={{ margin: "1px 0" }}>{truncateText(data?.notes, 40)}</p>
        </div>
      )}

      {data?.voidedAt && (
        <div style={{ marginBottom: "5mm", color: "red" }}>
          <h3 style={{ fontSize: "12px", margin: "2px 0" }}>ຍົກເລິກ</h3>
          <p style={{ margin: "1px 0" }}>
            ວັນທີ:{" "}
            {new Date(data?.voidedAt).toLocaleString("th-TH", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </p>
          <p style={{ margin: "1px 0" }}>
            ໂດຍ: {truncateText(data?.voidedBy?.name || "ບໍ່ລະບຸ", 30)}
          </p>
          <p style={{ margin: "1px 0" }}>
            ໝາຍເຫດ: {truncateText(data?.voidReason || "ບໍ່ລະບຸ", 30)}
          </p>
        </div>
      )}

      <Flex justify="center">
        <Barcode
          value={data?.receiptNumber}
          width={2}
          height={45}
          format="CODE128"
          displayValue={true}
          fontSize={12}
          // textAlign="center"
          // textPosition="bottom"
          textMargin={2}
        />
      </Flex>

      <p style={{ textAlign: "center", margin: "5mm 0 0 0", fontSize: "10px" }}>
        ຂອບໃຈທີ່ໃຊ້ບໍລິການ
      </p>
    </div>
  );
};

export default Bill;
